import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {SearchResult, ArticoloBase, Articolo} from '../_models/_Index';
import { environment } from '../../environments/environment';
import {Giacenza} from '../_models/Giacenza';
import {Magazzino} from '../_models/Magazzino';
import {MagazzinoArea} from '../_models/MagazzinoArea';
import {ArticoloEsolver} from "../_models/ArticoloEsolver";
import {ArticoloCiclo} from "../_models/ArticoloCiclo";
import {ArticoloCoeff} from "../_models/ArticoloCoeff";
import {UdM} from "../_models/UdM";
import { firstValueFrom } from 'rxjs';
import {ArtVarianteEsolver} from "../_models/ArtVarianteEsolver";
import {ArticoloVarianteLocale} from "../_models/ArticoloVarianteLocale";

@Injectable({ providedIn: 'root' })
export class ArticoliService {

  constructor(
    private http: HttpClient) {}

  // ── US6: anagrafica locale varianti (ProductApi /Varianti) ───────────────────
  async getVariantiByArticolo(orgId: number, articoloId: number) {
    return firstValueFrom(this.http.get<SearchResult<ArticoloVarianteLocale>>(
      `${environment.localApiUrl}product/Varianti/VariantiByArticolo?orgId=${orgId}&articoloId=${articoloId}`));
  }

  async createVariante(orgId: number, articoloId: number, variante: ArticoloVarianteLocale) {
    return firstValueFrom(this.http.post<ArticoloVarianteLocale>(
      `${environment.localApiUrl}product/Varianti/CreateVariante?orgId=${orgId}&articoloId=${articoloId}`, variante));
  }

  async updateVariante(orgId: number, articoloId: number, varianteId: number, variante: ArticoloVarianteLocale) {
    return firstValueFrom(this.http.post<ArticoloVarianteLocale>(
      `${environment.localApiUrl}product/Varianti/UpdateVariante?orgId=${orgId}&articoloId=${articoloId}&varianteId=${varianteId}`, variante));
  }

  async deleteVariante(orgId: number, articoloId: number, varianteId: number) {
    return firstValueFrom(this.http.get(
      `${environment.localApiUrl}product/Varianti/DeleteVariante?orgId=${orgId}&articoloId=${articoloId}&varianteId=${varianteId}`));
  }

  async getArticoliFilteredAsync(orgId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean) {
    return firstValueFrom(this.http.post<SearchResult<Articolo>>(`${environment.localApiUrl}product/Articoli/ArticoliFiltered?orgId=${orgId}`,
      { search, page, count, sortField, sortAsc }));
  }

  async getArticoloAsync(orgId: number, id: number) {
    return firstValueFrom(this.http.get<Articolo>(`${environment.localApiUrl}product/Articoli/ArticoloFromId?orgId=${orgId}&id=${id}`));
  }

  async createArticoloAsync(articolo: Articolo) {
    return firstValueFrom(this.http.post<Articolo>(`${environment.localApiUrl}product/Articoli/CreateArticolo`, articolo));
  }

  async updateArticoloAsync(articolo: Articolo) {
    return firstValueFrom(this.http.post<Articolo>(`${environment.localApiUrl}product/Articoli/UpdateArticolo`, articolo));
  }

  async deleteArticoloAsync(orgId: number, id: number) {
    return firstValueFrom(this.http.get<number>(`${environment.localApiUrl}product/Articoli/DeleteArticolo?orgId=${orgId}&id=${id}`));
  }
  
  async searchArticoli(orgId: number, search: string, page: number, count: number) {
    return firstValueFrom(this.http.post<SearchResult<ArticoloEsolver>>(`${environment.localApiUrl}api/Esolver/Articoli/${orgId}`,
      {search, page, count, sortField: 'CodArt', sortAsc: true}));
  }

  /**
   * Letture puntuali dell'anagrafica ESOLVER gia' fatte o in corso, per `orgId|codArt`.
   *
   * Si memorizza la PROMISE e non il risultato: app-lotto-input compare una volta per RIGA nelle
   * liste materiali e le istanze partono tutte insieme, quindi senza questo N richieste identiche
   * viaggerebbero in parallelo prima ancora che la prima risponda.
   */
  private readonly articoloEsolverCache = new Map<string, Promise<ArticoloEsolver | null>>();

  /**
   * Legge UN articolo ESOLVER per codice esatto, con cache. Non e' una ricerca travestita: lato
   * vista `searchArticoli` con count=1 filtra gia' per `CodArt == search` (uguaglianza, non
   * `Contains`), quindi questa e' una lettura puntuale e come tale si presta alla memorizzazione.
   *
   * Restituisce null quando l'articolo NON ESISTE, e rilancia quando la lettura FALLISCE: sono due
   * cose diverse e i chiamanti le trattano diversamente — c'e' chi su errore avvisa l'operatore e
   * chi tira dritto. Appiattirle qui cancellerebbe quella scelta.
   *
   * Cache per sessione, senza scadenza — l'anagrafica ESOLVER non cambia mentre l'operatore e' su
   * una schermata. Gli errori NON vengono memorizzati, cosi' il tentativo dopo riprova davvero.
   */
  async getArticoloEsolverByCodArt(orgId: number, codArt: string): Promise<ArticoloEsolver | null> {
    if (!codArt) return null;
    const chiave = `${orgId}|${codArt}`;
    const giaInCorso = this.articoloEsolverCache.get(chiave);
    if (giaInCorso) return giaInCorso;

    const richiesta = this.searchArticoli(orgId, codArt, 0, 1)
      .then((risultato: SearchResult<ArticoloEsolver>) => {
        const articolo = risultato?.result?.[0];
        // Il codice va confrontato comunque: l'endpoint resta una ricerca paginata.
        return articolo?.codArt === codArt ? articolo : null;
      })
      .catch((errore) => {
        this.articoloEsolverCache.delete(chiave);
        throw errore;
      });

    this.articoloEsolverCache.set(chiave, richiesta);
    return richiesta;
  }

  /** Svuota la cache delle letture puntuali (cambio organizzazione, o anagrafica ricaricata). */
  invalidaArticoliEsolver() {
    this.articoloEsolverCache.clear();
  }

  async getEntitiesFromOrdiniAsyn(entityType: string, orgid: number, search: string, page: number, count: number) {
    return firstValueFrom(this.http.post<SearchResult<ArticoloBase>>(`${environment.localApiUrl}api/Articoli/${entityType}/${orgid}`,
      { search, page, count, sortField: '', sortAsc: true }));
  }

  async getGiacenza(orgId: number, codArt: string, codMag: string, codMagArea: string, codLotto: string, risorsaId: number) {
    return firstValueFrom(this.http.post<SearchResult<Giacenza>>(`${environment.localApiUrl}api/Articoli/Giacenza/${orgId}`, 
      { codArt, codMag, codMagArea, codLotto, risorsaId }));
  }

  async getMagazzini(orgId: number, tipiAnagr: number) {
    return firstValueFrom(this.http.post<SearchResult<Magazzino>>(`${environment.localApiUrl}api/Articoli/Magazzini/${orgId}/${tipiAnagr}`, {}));
  }

  async getAreeOfMagazzino(orgId: number, codMag: string) {
    return firstValueFrom(this.http
      .post<SearchResult<MagazzinoArea>>(`${environment.localApiUrl}api/Articoli/AreeOfMagazzino/${orgId}`, { codMag }));
  }

  async getUdM(orgId: number, tipiUdM: number) {
    return firstValueFrom(this.http.post<SearchResult<UdM>>(`${environment.localApiUrl}api/Articoli/UdM/${orgId}/${tipiUdM}`, {}));
  }

  async searchVariantiOfArticolo(orgId: number, codArt: string) {
    return firstValueFrom(this.http.post<SearchResult<ArtVarianteEsolver>>(`${environment.localApiUrl}api/Articoli/VariantiOfArticolo/${orgId}`,
      {search: codArt, page: 0, count: 0, sortField: 'VarianteArt', sortAsc: true}));
  }
  
  async searchCicloOfArticolo(orgId: number, codArt: string, page: number, count: number) {
    return firstValueFrom(this.http.post<SearchResult<ArticoloCiclo>>(`${environment.localApiUrl}api/Articoli/CicloOfArticolo/${orgId}`,
      {search: codArt, page, count, sortField: 'CodCiclo', sortAsc: true}));
  }

  async searchCoeffOfArticolo(orgId: number, codArt: string, srcUm: string, desUm: string) {
    return firstValueFrom(this.http.post<ArticoloCoeff>(`${environment.localApiUrl}api/Articoli/CoeffOfArticolo/${orgId}`,
      {search: codArt, srcUm, desUm, page: 0, count: 0, sortField: 'CodArt', sortAsc: true}));
  }

  // ── Propagazione Articolo → Ordini Clienti (feature 121, issue #3372) ──
  // La scheda articolo parla SOLO con ProductApi: non esiste una seconda base URL verso SaleApi.

  /**
   * Conteggi di impatto in RIGHE d'ordine. `varianteIds` porta le varianti di cui il dialogo deve
   * poter mostrare i numeri (nel dialogo di variante, quella modificata).
   * Con SaleApi non raggiungibile — o più lento del budget — la risposta è `disponibile: false`,
   * mai un errore (spec FR-012, FR-018).
   */
  async anteprimaImpattoOrdiniArticoloAsync(orgId: number, articoloId: number, varianteIds: number[] = []) {
    const query = varianteIds.map(id => `&varianteIds=${id}`).join('');
    return firstValueFrom(this.http.get<AnteprimaImpattoOrdiniArticolo>(
      `${environment.localApiUrl}product/Articoli/AnteprimaImpattoOrdini`
      + `?orgId=${orgId}&articoloId=${articoloId}${query}`));
  }

  /**
   * Avvia la propagazione. Il corpo dichiara QUALI campi propagare, mai i valori: quelli vengono
   * riletti dal database di ProductApi (spec FR-030). La risposta porta il solo `correlationId`: il
   * conteggio che l'operatore ha letto è quello dell'anteprima, già in possesso del frontend.
   * Un `502` significa che la propagazione non è partita, oppure — con `avvioIncerto` nel corpo —
   * che non è stato possibile confermarne l'avvio (spec FR-043).
   */
  async propagaAiOrdiniArticoloAsync(request: PropagaAiOrdiniArticoloRequest) {
    return firstValueFrom(this.http.post<{ correlationId: string }>(
      `${environment.localApiUrl}product/Articoli/PropagaAiOrdini`, request));
  }
}

// Conteggi in RIGHE d'ordine (spec FR-011). I quattro ausiliari esistono in DUE varianti d'ambito
// perché il numero mostrato dev'essere esatto anche in "solo righe non consegnate" (FR-010).
// `ordiniTotali` è DIAGNOSTICO: non va mai mostrato nel dialogo.
export interface ImpattoConteggioArticolo {
  righeInCorso: number;
  righeTotali: number;
  righeConQuantitaTotali: number;
  righeConQuantitaInCorso: number;
  righeSenzaVarianteTotali: number;
  righeSenzaVarianteInCorso: number;
  ordiniTotali: number;
}

// Per COMPOSIZIONE e non per estensione, come i DTO backend: nel ramo variante i quattro conteggi
// ausiliari non sono calcolati e comparirebbero come zeri indistinguibili da un dato vero.
export interface ImpattoVarianteConteggio {
  varianteId: number;
  righeInCorso: number;
  righeTotali: number;
  ordiniTotali: number;
}

export interface AnteprimaImpattoOrdiniArticolo {
  disponibile: boolean;
  articolo?: ImpattoConteggioArticolo | null;
  varianti: ImpattoVarianteConteggio[];
}

// ⚠️ Solo NOMI di campo, mai valori; nessun `executedBy`: l'identità arriva dal token.
export interface PropagaAiOrdiniArticoloRequest {
  orgId: number;
  articoloId: number;
  soloInCorso: boolean;
  campiArticolo: string[];
  varianti: { varianteId: number; campi: string[] }[];
}
